import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchHealth } from '../api/client';
import { formatDate } from '../utils/formatters';

function StatusBadge({ status }) {
  const styles = {
    healthy: 'bg-success/15 text-success border-success/30',
    degraded: 'bg-yellow-sunny/15 text-yellow-sunny border-yellow-sunny/30',
    unhealthy: 'bg-alert/15 text-alert border-alert/30',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold border-2 ${
        styles[status] || styles.unhealthy
      }`}
    >
      <span
        className={`w-2.5 h-2.5 rounded-full ${
          status === 'healthy'
            ? 'bg-success'
            : status === 'degraded'
            ? 'bg-yellow-sunny'
            : 'bg-alert'
        }`}
      />
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
    </span>
  );
}

function CheckItem({ name, ok }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm font-medium text-text-primary">{name}</span>
      <span
        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
          ok ? 'text-success' : 'text-alert'
        }`}
      >
        <span className={`w-2.5 h-2.5 rounded-full ${ok ? 'bg-success' : 'bg-alert'}`} />
        {ok ? 'OK' : 'Error'}
      </span>
    </div>
  );
}

export default function Health() {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await fetchHealth(token);
      setData(result);
      setError(null);
      setLastRefresh(new Date().toISOString());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 30000);
    return () => clearInterval(interval);
  }, [loadHealth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-text-secondary">Loading health status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-alert/10 border-2 border-alert rounded-xl p-6 shadow-brutal">
        <p className="text-alert font-semibold">Failed to load health status</p>
        <p className="text-text-secondary text-sm mt-1">{error}</p>
        <button
          onClick={loadHealth}
          className="mt-3 text-sm text-primary font-medium hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const checks = data?.checks || {};
  const lambdas = data?.lambdas || [];
  const stepFunctions = data?.stepFunctions || [];
  const eventBridge = data?.eventBridge || {};
  const dynamodb = data?.dynamodb || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-extrabold text-text-primary">System Health</h2>
        <StatusBadge status={data?.status} />
      </div>

      {/* Environment + Checks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Component Checks */}
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal p-6">
          <h3 className="text-lg font-bold text-text-primary mb-4">Component Checks</h3>
          <div className="divide-y divide-ink/10">
            {Object.entries(checks).length === 0 ? (
              <p className="text-text-secondary text-sm py-2">No check data available.</p>
            ) : (
              Object.entries(checks).map(([name, result]) => (
                <CheckItem
                  key={name}
                  name={name}
                  ok={result === true || result?.ok === true || result?.status === 'ok' || result?.status === 'healthy'}
                />
              ))
            )}
          </div>
        </div>

        {/* Infrastructure */}
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal p-6">
          <h3 className="text-lg font-bold text-text-primary mb-4">Infrastructure</h3>
          <div className="space-y-3">
            {data?.environment && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Environment</span>
                <span className="text-sm font-semibold text-text-primary">{data.environment}</span>
              </div>
            )}
            {data?.region && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Region</span>
                <span className="text-sm font-semibold text-text-primary">{data.region}</span>
              </div>
            )}
            {eventBridge?.busName && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">EventBridge Bus</span>
                <span className="text-sm font-mono text-text-primary">{eventBridge.busName}</span>
              </div>
            )}
            {(dynamodb?.tableName || data?.dynamoTable) && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">DynamoDB Table</span>
                <span className="text-sm font-mono text-text-primary">
                  {dynamodb.tableName || data.dynamoTable}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lambda Functions */}
      {lambdas.length > 0 && (
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal overflow-hidden">
          <div className="px-6 py-4 border-b-2 border-ink/10">
            <h3 className="text-lg font-bold text-text-primary">
              Lambda Functions ({lambdas.length})
            </h3>
          </div>
          <div className="divide-y divide-ink/10">
            {lambdas.map((fn, i) => (
              <div key={fn.name || fn.functionName || i} className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm font-mono text-text-primary">
                  {fn.name || fn.functionName}
                </span>
                {fn.runtime && (
                  <span className="text-xs text-text-secondary bg-background px-2 py-0.5 rounded border border-ink/10">{fn.runtime}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step Functions */}
      {stepFunctions.length > 0 && (
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal overflow-hidden">
          <div className="px-6 py-4 border-b-2 border-ink/10">
            <h3 className="text-lg font-bold text-text-primary">
              Step Functions ({stepFunctions.length})
            </h3>
          </div>
          <div className="divide-y divide-ink/10">
            {stepFunctions.map((sf, i) => (
              <div key={sf.name || sf.stateMachineArn || i} className="px-6 py-3 flex items-center justify-between">
                <span className="text-sm font-mono text-text-primary">
                  {sf.name || sf.stateMachineArn}
                </span>
                {sf.status && (
                  <span
                    className={`text-xs font-semibold ${
                      sf.status === 'ACTIVE' ? 'text-success' : 'text-text-secondary'
                    }`}
                  >
                    {sf.status}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <p className="text-xs text-text-secondary text-center">
        Last checked: {lastRefresh ? formatDate(lastRefresh) : 'N/A'} &middot; Auto-refresh every 30s
      </p>
    </div>
  );
}
